import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { create } from 'zustand';

export type ConnectionHealth = 'healthy' | 'warning' | 'error' | 'disconnected';
export type IndexingStatus = 'synced' | 'lagging' | 'far_behind';

export const INDEXING_DELAY_MESSAGE = msg({
  comment: 'Alert shown while blockchain indexing lags',
  message:
    "We're experiencing a temporary delay as blockchain data syncs. Market and account information may not be current, and order placement is paused.",
});

// Status/tooltip texts are Lingui descriptors so the footer resolves them against the
// active locale at render time — the store has no i18n instance and must not bake in strings.
const STATUS_TEXT = {
  operational: msg({ comment: 'Footer connection status', message: 'Operational' }),
  disconnected: msg({ comment: 'Footer connection status', message: 'Disconnected' }),
  connectionIssues: msg({ comment: 'Footer connection status', message: 'Connection Issues' }),
  slowConnection: msg({ comment: 'Footer connection status', message: 'Slow Connection' }),
};

// Block counts are pluralized rather than interpolated into a bare "blocks": languages with more
// than two plural categories (ru, pl, ar…) need a separate form for 1 / 2–4 / 5+, and a translator
// can only reach those forms if the source message declares the plural.
const indexingStatusText = (lag: number) =>
  msg({
    comment: 'Footer connection status; the number is a block count',
    message: plural({ lag }, { one: 'Indexing (# block behind)', other: 'Indexing (# blocks behind)' }),
  });

const ISSUE_TEXT = {
  marketDisconnected: msg({ comment: 'Connection status tooltip line', message: 'Market data disconnected' }),
  tradingDisconnected: msg({ comment: 'Connection status tooltip line', message: 'Trading connection disconnected' }),
  marketError: msg({ comment: 'Connection status tooltip line', message: 'Market data connection error' }),
  tradingError: msg({ comment: 'Connection status tooltip line', message: 'Trading connection error' }),
  marketSlow: msg({ comment: 'Connection status tooltip line', message: 'Market data connection slow' }),
  tradingSlow: msg({ comment: 'Connection status tooltip line', message: 'Trading connection slow' }),
  allOperational: msg({ comment: 'Connection status tooltip line', message: 'All systems operational' }),
};

const marketBehindText = (lag: number) =>
  msg({
    comment: 'Connection status tooltip line; the number is a block count',
    message: plural({ lag }, { one: 'Market data # block behind', other: 'Market data # blocks behind' }),
  });

const accountBehindText = (lag: number) =>
  msg({
    comment: 'Connection status tooltip line; the number is a block count',
    message: plural({ lag }, { one: 'Account data # block behind', other: 'Account data # blocks behind' }),
  });

// Derived connection status for UI - computed only when health changes
export interface ConnectionStatusSummary {
  overallHealth: ConnectionHealth;
  statusText: MessageDescriptor;
  tooltipText: MessageDescriptor[];
}

interface ConnectionState {
  lastHeartbeatTime: number | null;
  lastSequenceNumber: number | null;
  headBlockNumber: number | null;
  connectionHealth: ConnectionHealth;
  indexingStatus: IndexingStatus;
  indexingLagBlocks: number;
}

interface HeartbeatState {
  isAuthenticated: boolean;
  isIndexingFarBehind: boolean;
  // Market data WebSocket
  marketData: ConnectionState;

  /**
   * Trading WebSocket. Deliberately just connection health: the per-request order timeout windows
   * that used to live here are fields on `OpenOrdersStore.InFlightRequest` now.
   */
  trading: ConnectionState;

  // Computed connection status summary (memoized)
  connectionStatusSummary: ConnectionStatusSummary;

  // Actions
  updateMarketDataHeartbeat: (sn: number, h: number) => void;
  updateTradingHeartbeat: (sn: number, h: number) => void;
  checkTimeout: (kind: 'marketData' | 'trading') => ConnectionHealth;
  resetMarketData: () => void;
  resetTrading: () => void;
  updateConnectionStatusSummary: () => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
}

const HEARTBEAT_WARNING_TIMEOUT = 5_000;
const HEARTBEAT_ERROR_TIMEOUT = 30_000;
const INDEXING_LAG_THRESHOLD = 10; // blocks

// Helper to compute connection status summary
export function computeConnectionStatusSummary(
  marketDataHealth: ConnectionHealth,
  tradingHealth: ConnectionHealth,
  marketIndexingStatus: IndexingStatus,
  tradingIndexingStatus: IndexingStatus,
  marketIndexingLag: number,
  tradingIndexingLag: number,
  isAuthenticated: boolean = false
): ConnectionStatusSummary {
  const effectiveTradingHealth = !isAuthenticated ? 'healthy' : tradingHealth;

  // Worst health determines overall status
  const overallHealth: ConnectionHealth =
    marketDataHealth === 'error' || effectiveTradingHealth === 'error'
      ? 'error'
      : marketDataHealth === 'warning' || effectiveTradingHealth === 'warning'
        ? 'warning'
        : marketDataHealth === 'disconnected' || effectiveTradingHealth === 'disconnected'
          ? 'disconnected'
          : 'healthy';

  let statusText = STATUS_TEXT.operational;
  if (overallHealth === 'disconnected') statusText = STATUS_TEXT.disconnected;
  else if (overallHealth === 'error') statusText = STATUS_TEXT.connectionIssues;
  else if (overallHealth === 'warning') statusText = STATUS_TEXT.slowConnection;
  else if (marketIndexingStatus === 'far_behind' || tradingIndexingStatus === 'far_behind') {
    statusText = indexingStatusText(Math.max(marketIndexingLag, tradingIndexingLag));
  }

  const issues: MessageDescriptor[] = [];
  if (marketDataHealth === 'disconnected') issues.push(ISSUE_TEXT.marketDisconnected);
  if (effectiveTradingHealth === 'disconnected') issues.push(ISSUE_TEXT.tradingDisconnected);
  if (marketDataHealth === 'error') issues.push(ISSUE_TEXT.marketError);
  if (effectiveTradingHealth === 'error') issues.push(ISSUE_TEXT.tradingError);
  if (marketDataHealth === 'warning') issues.push(ISSUE_TEXT.marketSlow);
  if (effectiveTradingHealth === 'warning') issues.push(ISSUE_TEXT.tradingSlow);
  if (marketIndexingStatus === 'far_behind') issues.push(marketBehindText(marketIndexingLag));
  if (tradingIndexingStatus === 'far_behind') issues.push(accountBehindText(tradingIndexingLag));

  const tooltipText = issues.length > 0 ? issues : [ISSUE_TEXT.allOperational];

  return { overallHealth, statusText, tooltipText };
}

export const useHeartbeatStore = create<HeartbeatState>((set, get) => ({
  isAuthenticated: false,
  isIndexingFarBehind: false,
  marketData: {
    lastHeartbeatTime: null,
    lastSequenceNumber: null,
    headBlockNumber: null,
    connectionHealth: 'disconnected',
    indexingStatus: 'synced',
    indexingLagBlocks: 0,
  },

  trading: {
    lastHeartbeatTime: null,
    lastSequenceNumber: null,
    headBlockNumber: null,
    connectionHealth: 'disconnected',
    indexingStatus: 'synced',
    indexingLagBlocks: 0,
  },

  connectionStatusSummary: {
    overallHealth: 'disconnected',
    statusText: STATUS_TEXT.disconnected,
    tooltipText: [ISSUE_TEXT.marketDisconnected],
  },

  updateConnectionStatusSummary: () => {
    const state = get();
    const newSummary = computeConnectionStatusSummary(
      state.marketData.connectionHealth,
      state.trading.connectionHealth,
      state.marketData.indexingStatus,
      state.trading.indexingStatus,
      state.marketData.indexingLagBlocks,
      state.trading.indexingLagBlocks,
      state.isAuthenticated
    );
    const isIndexingFarBehind =
      state.marketData.indexingStatus === 'far_behind' || state.trading.indexingStatus === 'far_behind';
    set({ connectionStatusSummary: newSummary, isIndexingFarBehind });
  },

  updateMarketDataHeartbeat: (sn: number, h: number) => {
    const now = Date.now();
    const state = get();

    // Calculate indexing lag
    const indexingLag = h - sn;
    const indexingStatus: IndexingStatus = indexingLag >= INDEXING_LAG_THRESHOLD ? 'far_behind' : 'synced';

    const newMarketData = {
      ...state.marketData,
      lastHeartbeatTime: now,
      lastSequenceNumber: sn,
      headBlockNumber: h,
      connectionHealth: 'healthy' as ConnectionHealth,
      indexingStatus,
      indexingLagBlocks: indexingLag,
    };
    set({ marketData: newMarketData });
    get().updateConnectionStatusSummary();
  },

  updateTradingHeartbeat: (sn: number, h: number) => {
    const now = Date.now();
    const state = get();

    // Calculate indexing lag
    const indexingLag = h - sn;
    const indexingStatus: IndexingStatus = indexingLag >= INDEXING_LAG_THRESHOLD ? 'far_behind' : 'synced';

    const newTrading = {
      ...state.trading,
      lastHeartbeatTime: now,
      lastSequenceNumber: sn,
      headBlockNumber: h,
      connectionHealth: 'healthy' as ConnectionHealth,
      indexingStatus,
      indexingLagBlocks: indexingLag,
    };
    set({ trading: newTrading });
    get().updateConnectionStatusSummary();
  },

  checkTimeout: (kind: 'marketData' | 'trading') => {
    const state = get();
    const current = state[kind];
    const { lastHeartbeatTime } = current;
    if (!lastHeartbeatTime) return 'disconnected';

    const elapsed = Date.now() - lastHeartbeatTime;
    let newHealth: ConnectionHealth;

    if (elapsed > HEARTBEAT_ERROR_TIMEOUT) {
      newHealth = 'error';
    } else if (elapsed > HEARTBEAT_WARNING_TIMEOUT) {
      newHealth = 'warning';
    } else {
      return 'healthy'; // No update needed
    }

    if (current.connectionHealth !== newHealth) {
      if (kind === 'marketData') {
        set({ marketData: { ...current, connectionHealth: newHealth } });
      } else {
        set({ trading: { ...current, connectionHealth: newHealth } });
      }
      get().updateConnectionStatusSummary();
    }

    return newHealth;
  },

  resetMarketData: () => {
    set({
      marketData: {
        lastHeartbeatTime: null,
        lastSequenceNumber: null,
        headBlockNumber: null,
        connectionHealth: 'disconnected',
        indexingStatus: 'synced',
        indexingLagBlocks: 0,
      },
    });
    get().updateConnectionStatusSummary();
  },

  /**
   * Resets the stream's own health and nothing else. Wiping the order timeout windows here left
   * every request that straddled a reconnect unjudgeable; deadlines live on the request now.
   */
  resetTrading: () => {
    set({
      trading: {
        lastHeartbeatTime: null,
        lastSequenceNumber: null,
        headBlockNumber: null,
        connectionHealth: 'disconnected',
        indexingStatus: 'synced',
        indexingLagBlocks: 0,
      },
    });
    get().updateConnectionStatusSummary();
  },

  setAuthenticated: (isAuthenticated: boolean) => {
    set({ isAuthenticated });
    get().updateConnectionStatusSummary();
  },
}));
